import { type ReactNode } from 'react';
import { useGT } from 'gt-react';
import { useApExceptionRuleSets } from '@/api/rpc/hooks/accounts-payable/useApExceptionRuleSets';
import { SelectDropdown } from '@/components/common/SelectDropdown';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  resolveSelectedRuleSetId,
  ruleSetOptionLabel,
  ruleSetOptions,
  ruleSetPickerState,
} from './testExceptionRules';

/**
 * Shared rule-set dropdown + Run button used by the live re-evaluate and
 * dry-run exception dialogs. Copy stays with the caller so each surface keeps
 * its own translation context.
 */
export function ExceptionRuleSetPicker({
  ruleSetId,
  isRunning,
  onChange,
  onRun,
  copy,
}: {
  ruleSetId: string | null;
  isRunning: boolean;
  onChange: (ruleSetId: string | null) => void;
  onRun: (ruleSetId: string) => void;
  copy: {
    fieldLabel: ReactNode;
    loadingPlaceholder: string;
    selectPlaceholder: string;
    searchPlaceholder: string;
    emptyHint: ReactNode;
    runBusy: ReactNode;
    runIdle: ReactNode;
  };
}) {
  const gt = useGT();
  const { data, isLoading } = useApExceptionRuleSets();
  const options = ruleSetOptions(data?.ruleSets ?? [], (params) =>
    ruleSetOptionLabel(gt, params),
  );
  const selectedId = resolveSelectedRuleSetId(
    ruleSetId,
    options.map((option) => option.id),
  );
  const selected = options.find((option) => option.id === selectedId) ?? null;
  const state = ruleSetPickerState({
    isLoading,
    isRunning,
    optionCount: options.length,
    selectedId,
  });
  const placeholder =
    state.placeholderKey === 'loading'
      ? copy.loadingPlaceholder
      : copy.selectPlaceholder;

  return (
    <div className="space-y-1">
      <Label className="text-xs">{copy.fieldLabel}</Label>
      <div className="flex gap-2">
        <SelectDropdown
          options={options}
          value={selected}
          onChange={(option) => onChange(option?.id ?? null)}
          placeholder={placeholder}
          searchPlaceholder={copy.searchPlaceholder}
          size="sm"
          fullWidth
          disabled={state.disabled}
        />
        <Button
          size="sm"
          disabled={!state.canRun}
          onClick={() => selectedId && onRun(selectedId)}
        >
          {isRunning ? copy.runBusy : copy.runIdle}
        </Button>
      </div>
      {state.showEmptyHint ? (
        <p className="text-xs text-muted-foreground">{copy.emptyHint}</p>
      ) : null}
    </div>
  );
}
